// src/components/TypeManager.js
import React, { useEffect, useState, useContext } from 'react';
import {
	Box,
	Button,
	IconButton,
	List,
	ListItem,
	ListItemText,
	TextField,
	Typography,
	CircularProgress,
	Alert,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
} from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import { http, ConfigContext } from '../../../../../base-ui/lib';
import { toastError, toastSuccess } from '../../../../../Toaster/Toast';
import AppAutocomplete from 'src/globalComponents/AppAutoComplete';

const TypeManager = ({ type, title }) => {
	const [items, setItems] = useState([]);
	const [newItemName, setNewItemName] = useState('');
	const [selectedLimiters, setSelectedLimiters] = useState([]);
	const [loading, setLoading] = useState(false);
	const [limitersOptions, setLimitersOptions] = useState([]);
	const [limitersLoading, setLimitersLoading] = useState(false);
	const [error, setError] = useState(null);
	const { config } = useContext(ConfigContext);

	// State for editing
	const [editingItem, setEditingItem] = useState(null);
	const [editDialogOpen, setEditDialogOpen] = useState(false);

	// Fetch items on component mount
	useEffect(() => {
		fetchItems();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Fetch available limiters if managing assembly types
	useEffect(() => {
		if (type === 'assemblyType') {
			fetchLimiters();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const fetchItems = async () => {
		setLoading(true);
		try {
			const response = await http.get(
				{ pageSize: 50, pageOffset: 0, sortBy: 'name', sortOrder: 1 },
				`${config.uri.base}/${type}`
			);
			setItems(response.rows || response.data); // Adjust based on your API response structure
		} catch (error) {
			console.error(`Error fetching ${type}:`, error);
			setError(`Error fetching ${title}.`);
		} finally {
			setLoading(false);
		}
	};

	const fetchLimiters = async () => {
		setLimitersLoading(true);
		try {
			const response = await http.get(
				{ pageSize: 100, pageOffset: 0, sortBy: 'name', sortOrder: 1 },
				`${config.uri.base}/limiters`
			);
			setLimitersOptions(response.rows || response.data); // Adjust based on your API response structure
		} catch (error) {
			console.error('Error fetching limiters:', error);
			setError('Error fetching Limiters.');
		} finally {
			setLimitersLoading(false);
		}
	};

	const handleAdd = async () => {
		if (!newItemName.trim()) return;

		const payload = {
			name: newItemName.trim(),
		};

		if (type === 'assemblyType') {
			payload.limiters = selectedLimiters.map((limiter) => limiter._id);
		}

		try {
			const response = await http.post(payload, `${config.uri.base}/${type}`);
			if (response.errored)
				return toastError({
					title: response.error.message,
				});

			setItems((prev) => [...prev, response]); // Adjust based on your API response
			toastSuccess({
				title: `${title} added successfully.`,
			});
		} catch (error) {
			console.error(`Error adding ${type}:`, error);
			toastError({
				title: `An error occurred while adding ${title}.`,
			});
		} finally {
			setNewItemName('');
			setSelectedLimiters([]);
		}
	};
	// #FEATURE TO BE USED IN FUTURE
	// const handleDelete = async (id) => {
	//     try {
	//         await http.delete({}, `${config.uri.base}/${type}/${id}`);
	//         setItems(items.filter((item) => item._id !== id));
	//     } catch (error) {
	//         console.error(`Error deleting ${type}:`, error);
	//         setError(`Error deleting ${title}.`);
	//     }
	// };

	// Handle Edit
	const handleEditClick = (item) => {
		setEditingItem(item);
		setEditDialogOpen(true);
	};

	const handleEditSave = async () => {
		if (!editingItem.name.trim()) return;

		const payload = {
			name: editingItem.name.trim(),
		};

		if (type === 'assemblyType') {
			payload.limiters = editingItem.limiters.map((limiter) => limiter._id);
		}

		try {
			const response = await http.put(
				payload,
				`${config.uri.base}/${type}/${editingItem._id}`
			);
			if (response.errored)
				return toastError({
					title: 'Error',
					description: `${response.error.message}`,
				});

			setItems(
				items.map((item) => (item._id === response._id ? response : item))
			);
			toastSuccess({
				title: `${title} updated successfully.`,
			});
		} catch (error) {
			console.error(`Error updating ${type}:`, error);
			toastError({
				title: 'Error',
				description: `An error occurred while updating ${title}.`,
			});
		} finally {
			setEditDialogOpen(false);
			setEditingItem(null);
		}
	};

	const handleEditCancel = () => {
		setEditDialogOpen(false);
		setEditingItem(null);
	};

	if (loading)
		return (
			<Box display="flex" justifyContent="center" mt={2}>
				<CircularProgress size={24} />
			</Box>
		);

	if (error)
		return (
			<Alert severity="error" sx={{ mb: 2 }}>
				{error}
			</Alert>
		);

	return (
		<Box sx={{ mb: 4 }}>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					mb: 2,
				}}
			>
				<Typography variant="h6">{title}</Typography>
				<Box sx={{ display: 'flex', alignItems: 'center' }}>
					<TextField
						label={`Add ${title.slice(0, -1)}`}
						variant="outlined"
						size="small"
						value={newItemName}
						onChange={(e) => setNewItemName(e.target.value)}
						sx={{ mr: 1 }}
					/>
					{type === 'assemblyType' && (
						<AppAutocomplete
							multiple
							options={limitersOptions}
							getOptionLabel={(option) => option.name}
							loading={limitersLoading}
							value={selectedLimiters}
							onChange={(event, newValue) => {
								setSelectedLimiters(newValue);
							}}
							renderInput={(params) => (
								<TextField
									{...params}
									variant="outlined"
									size="small"
									label="Select Limiters"
									placeholder="Limiters"
									InputProps={{
										...params.InputProps,
										endAdornment: (
											<>
												{limitersLoading ? (
													<CircularProgress color="inherit" size={20} />
												) : null}
												{params.InputProps.endAdornment}
											</>
										),
									}}
								/>
							)}
							sx={{ width: 300, mr: 1 }}
						/>
					)}
					<Button variant="contained" size="small" onClick={handleAdd}>
						Add
					</Button>
				</Box>
			</Box>
			<List sx={{ padding: 0 }}>
				{items.map((item) => (
					<ListItem
						key={item._id}
						sx={{
							py: 0.5,
							fontSize: '0.875rem',
							borderBottom: '1px solid #e0e0e0',
						}}
						secondaryAction={
							<Box>
								<IconButton
									edge="end"
									aria-label="edit"
									onClick={() => handleEditClick(item)}
								>
									<EditIcon />
								</IconButton>
								{/* FEATURE TO BE USED IN FUTURE */}
								{/* <IconButton edge="end" aria-label="delete" onClick={() => handleDelete(item._id)}>
                                    <DeleteIcon />
                                </IconButton> */}
							</Box>
						}
					>
						<ListItemText
							primary={item.name}
							secondary={
								type === 'assemblyType' &&
								item.limiters &&
								item.limiters.length > 0
									? `Limiters: ${item.limiterDetails.map((lim) => lim.name).join(', ')}`
									: null
							}
						/>
					</ListItem>
				))}
				{loading && <Typography>Loading...</Typography>}
			</List>

			{/* Edit Dialog */}
			<Dialog
				open={editDialogOpen}
				onClose={handleEditCancel}
				fullWidth
				maxWidth="sm"
			>
				<DialogTitle>Edit {title.slice(0, -1)}</DialogTitle>
				<DialogContent>
					{editingItem && (
						<Box sx={{ mt: 2 }}>
							<TextField
								label="Name"
								variant="outlined"
								fullWidth
								value={editingItem.name}
								onChange={(e) =>
									setEditingItem({ ...editingItem, name: e.target.value })
								}
								sx={{ mb: 2 }}
							/>
							{type === 'assemblyType' && (
								<AppAutocomplete
									multiple
									options={limitersOptions}
									getOptionLabel={(option) => option.name}
									loading={limitersLoading}
									value={editingItem.limiterDetails}
									onChange={(event, newValue) => {
										setEditingItem({
											...editingItem,
											limiters: newValue,
											limiterDetails: newValue,
										});
									}}
									renderInput={(params) => (
										<TextField
											{...params}
											variant="outlined"
											label="Select Limiters"
											placeholder="Limiters"
											InputProps={{
												...params.InputProps,
												endAdornment: (
													<>
														{limitersLoading ? (
															<CircularProgress color="inherit" size={20} />
														) : null}
														{params.InputProps.endAdornment}
													</>
												),
											}}
										/>
									)}
									sx={{ width: '100%' }}
								/>
							)}
						</Box>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={handleEditCancel} color="secondary">
						Cancel
					</Button>
					<Button onClick={handleEditSave} variant="contained" color="primary">
						Save
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default TypeManager;
